import React, {useCallback, useEffect} from 'react';
import PageTitle from '../shared/PageTitle';
import {createSurface, deleteVenue, getVenue} from '../../api/adminAPI';
import {Venue} from '../../interfaces';
import {useTranslation} from 'react-i18next';
import {Helmet} from 'react-helmet';
import BackButton from '../shared/BackButton';
import {useNavigate, useParams} from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Modal,
  TextField,
  Typography,
} from '@mui/material';
import SurfacesTable from './SurfacesTable';
import styles from '../../styles';
import UpdateVenueModal from './UpdateVenueModal';

const AdminVenueDetail = () => {
  const {t} = useTranslation();
  const {venueId} = useParams();
  const navigate = useNavigate();

  const [venue, setVenue] = React.useState<Venue | null>(null);
  const [showAddSurfaceDialog, setShowAddSurfaceDialog] = React.useState(false);
  const [pending, setPending] = React.useState(true);
  const [addSurfaceName, setAddSurfaceName] = React.useState('');
  const [addSurfaceLivebarnStreamName, setAddSurfaceLivebarnStreamName] =
    React.useState('');

  const fetchData = useCallback(async () => {
    setPending(true);
    const venueData = await getVenue(parseInt(venueId ?? ''));
    setVenue(venueData);
    setPending(false);
  }, [venueId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAddSurfaceClick = useCallback(() => {
    setShowAddSurfaceDialog(true);
  }, []);

  const handleAddSurfaceClose = useCallback(() => {
    setShowAddSurfaceDialog(false);
    setAddSurfaceName('');
    setAddSurfaceLivebarnStreamName('');
  }, []);

  const handleOnAddSurfaceNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setAddSurfaceName(e.target.value);
    },
    [],
  );

  const handleOnAddSurfaceLivebarnStreamNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setAddSurfaceLivebarnStreamName(e.target.value);
    },
    [],
  );

  const handleAddSurfaceSubmit = useCallback(async () => {
    setPending(true);
    await createSurface({
      name: addSurfaceName,
      venueId: parseInt(venueId ?? ''),
      livebarnStreamName: addSurfaceLivebarnStreamName || undefined,
    });
    handleAddSurfaceClose();
    fetchData();
  }, [
    addSurfaceLivebarnStreamName,
    addSurfaceName,
    fetchData,
    handleAddSurfaceClose,
    venueId,
  ]);

  const handleDeleteVenue = useCallback(async () => {
    if (window.confirm(t('admin.confirmDeleteVenue') as string)) {
      await deleteVenue({id: parseInt(venueId ?? '')});
      navigate('/admin/venues');
    }
  }, [navigate, t, venueId]);

  return (
    <>
      <Helmet>
        <title>
          {t('navigation.pageTitle', {
            pageName: t('pages.venueDetail', {
              venueName: venue?.name,
            }),
          })}
        </title>
      </Helmet>
      <Box>
        <PageTitle>
          {t('pages.venueDetail', {
            venueName: venue?.name,
          })}
        </PageTitle>
        <BackButton navigationUrl="/admin/venues">
          {t('pages.venues')}
        </BackButton>
        {pending && !venue && (
          <Box sx={{display: 'flex', justifyContent: 'center', my: 4}}>
            <CircularProgress />
          </Box>
        )}
        {venue ? (
          <UpdateVenueModal
            venue={venue}
            loading={pending}
            onUpdate={fetchData}
          />
        ) : (
          <Button sx={{mb: 2, mr: 2}} disabled={true}>
            {t('admin.updateVenue')}
          </Button>
        )}
        <Button
          sx={{mb: 2, mr: 2}}
          onClick={handleAddSurfaceClick}
          disabled={pending}>
          {t('admin.addSurface')}
        </Button>
        <Button
          sx={{mb: 2}}
          color="error"
          onClick={handleDeleteVenue}
          disabled={pending}>
          {t('admin.deleteVenue')}
        </Button>
        <Modal
          open={showAddSurfaceDialog}
          onClose={handleAddSurfaceClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description">
          <Box sx={styles.modal}>
            <Typography variant="h4" sx={{mb: 2}}>
              {t('admin.addSurface')}
            </Typography>
            <Box component="form">
              <TextField
                label={t('general.name')}
                name="name"
                variant="outlined"
                value={addSurfaceName}
                onChange={handleOnAddSurfaceNameChange}
                sx={{mb: 2}}
                fullWidth
              />
              <TextField
                label={t('admin.livebarnStreamName')}
                name="livebarnStreamName"
                variant="outlined"
                value={addSurfaceLivebarnStreamName}
                onChange={handleOnAddSurfaceLivebarnStreamNameChange}
                sx={{mb: 2}}
                fullWidth
              />
              <Button
                disabled={pending || addSurfaceName.length === 0}
                onClick={handleAddSurfaceSubmit}>
                {t('general.submit')}
              </Button>
            </Box>
          </Box>
        </Modal>
        {venue && (
          <Box sx={{mt: 2}}>
            <Typography variant="body1" sx={{mb: 1}}>
              <strong>{t('admin.address')}:</strong> {venue.address || 'N/A'}
            </Typography>
            <Typography variant="body1" sx={{mb: 1}}>
              <strong>{t('admin.city')}:</strong> {venue.city || 'N/A'}
            </Typography>
            <Typography variant="body1" sx={{mb: 1}}>
              <strong>{t('admin.stateOrProvince')}:</strong>{' '}
              {venue.stateOrProvince || 'N/A'}
            </Typography>
            <Typography variant="body1" sx={{mb: 1}}>
              <strong>{t('admin.country')}:</strong> {venue.country || 'N/A'}
            </Typography>
            <Typography variant="body1" sx={{mb: 1}}>
              <strong>{t('admin.coordinates')}:</strong>{' '}
              {venue.lat && venue.lon ? `${venue.lat}, ${venue.lon}` : 'N/A'}
            </Typography>
          </Box>
        )}
        <Typography variant="h4" sx={{my: 2}}>
          {t('admin.surfaces')}
        </Typography>
        <SurfacesTable surfaces={venue?.surfaces ?? []} onUpdate={fetchData} />
      </Box>
    </>
  );
};

export default AdminVenueDetail;
